import { useEffect, useState, useRef } from 'react';
import { type OperationVariables } from '@apollo/react-common';
// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions
import { useSubscription } from '@apollo/react-hooks';
import { type GraphQLTaggedNode, useRelayEnvironment } from 'react-relay';
import { requestSubscription, type Disposable } from 'relay-runtime';
import type { DocumentNode } from 'graphql';
import type ApolloClient from 'apollo-client';

import { isRelayEnabled } from './isRelayEnabled.ts';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import { toDocumentNode } from './toDocumentNode.ts';
import { useMemoObj } from './useMemoObj.ts';

export type UseSubscriptionInteropSupportedResult<TData> = {
	data?: TData;
	loading: boolean;
	error?: ApolloErrorInterop;
};

export type SubscriptionDataCallback<TData> = (options: {
	subscriptionData: {
		data?: TData;
		loading: boolean;
	};
}) => void;

export type UseSubscriptionInteropSupportedHookOptions<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = {
	variables?: TVariables;
	skip?: boolean;
	onSubscriptionData?: SubscriptionDataCallback<TData>;
	onSubscriptionComplete?: () => void;
	/**
	 * Operation-level GraphQL `extensions` (e.g. AGG WebSocket replay metadata produced
	 * by `useSubscriptionValues`). Forwarded to the wire payload's `extensions` field via:
	 *   - Apollo build: smuggled as `variables.__extensions` and re-attached to the
	 *     operation context by `AttachExtensionsLink` (Apollo v2's `useSubscription` does
	 *     not forward a per-call `context` option, so the variable hack is still needed
	 *     here — but it stays inside this hook so consumers don't need to know).
	 *   - Relay build: forwarded via `cacheConfig.metadata.extensions`, which
	 *     `RelayWebSocketSubscriptionHandler` reads when building the WS payload.
	 */
	extensions?: Record<string, any>;
	/**
	 * Optional Apollo client to use for subscriptions.
	 * Only used in Apollo mode (non-Relay builds).
	 * In Relay mode, this option is ignored as subscriptions use the Relay environment.
	 */
	client?: ApolloClient<object>;
	/**
	 * Ensures that the interop in Apollo build variant uses the AGG client.
	 * Only used in Apollo mode (non-Relay builds).
	 * In Relay mode, this option is ignored as subscriptions use the Relay environment.
	 */
	interopApolloAggClient?: boolean;
	/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
	__callerPackage__?: string;
};

export type SubscriptionNode = GraphQLTaggedNode | DocumentNode;

const __TEST_ONLY_isTestOnlySafeEnvironment = (): boolean => {
	const isProduction = process.env.NODE_ENV === 'production';
	const isCloudProduction = process.env.CLOUD_ENV === 'production';
	const isCloudStaging = process.env.CLOUD_ENV === 'staging';

	if (isProduction || isCloudProduction || isCloudStaging) {
		return false;
	}

	const isTest = process.env.NODE_ENV === 'test';
	const isDevelopment = process.env.NODE_ENV === 'development';
	const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost';

	return isTest || isDevelopment || isLocalhost;
};

export const __TEST_ONLY_setForceRelayMode = (forceRelay: boolean): boolean => {
	if (!__TEST_ONLY_isTestOnlySafeEnvironment()) {
		return false;
	}

	(window as any).__TEST_ONLY_FORCE_RELAY_MODE__ = forceRelay;
	return true;
};

export function useSubscriptionInterop<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(
	subscription: SubscriptionNode,
	options?: UseSubscriptionInteropSupportedHookOptions<TData, TVariables>,
): UseSubscriptionInteropSupportedResult<TData> {
	const forceRelayMode = (window as any).__TEST_ONLY_FORCE_RELAY_MODE__;
	const shouldUseRelay = forceRelayMode || isRelayEnabled(options?.__callerPackage__);

	if (shouldUseRelay) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		return useSubscriptionInteropRelay<TData, TVariables>(
			subscription as GraphQLTaggedNode,
			options,
		);
	} else {
		const { interopApolloAggClient, extensions, ...apolloOptions } = options ?? {};

		// Apollo v2's `useSubscription` does not forward a per-call `context` option down
		// to the WebSocket link, so we smuggle `extensions` through `variables.__extensions`.
		// `AttachExtensionsLink` strips it back out and re-attaches it to the operation
		// context, which `GraphQLWSLink.request` then sets on the wire payload.
		const variables =
			extensions !== undefined
				? ({
						...(apolloOptions.variables ?? {}),
						__extensions: extensions,
					} as unknown as TVariables)
				: apolloOptions.variables;

		// eslint-disable-next-line react-hooks/rules-of-hooks
		return useSubscription<TData, TVariables>(toDocumentNode(subscription), {
			...apolloOptions,
			variables,
			...(interopApolloAggClient ? { client: (window as any)['GLOBAL_APOLLO_AGG_CLIENT'] } : {}),
		});
	}
}

function useSubscriptionInteropRelay<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(
	subscription: GraphQLTaggedNode,
	options?: UseSubscriptionInteropSupportedHookOptions<TData, TVariables>,
): UseSubscriptionInteropSupportedResult<TData> {
	const environment = useRelayEnvironment();
	const [data, setData] = useState<TData | undefined>(undefined);
	const [error, setError] = useState<ApolloErrorInterop | undefined>(undefined);
	const [loading, setLoading] = useState(!options?.skip);
	const disposableRef = useRef<Disposable | null>(null);

	const callbacksRef = useRef(options);
	callbacksRef.current = options;

	const skip = options?.skip;
	const variables = useMemoObj<TVariables>(options?.variables, options?.__callerPackage__);
	const extensions = useMemoObj<Record<string, any>>(
		options?.extensions,
		options?.__callerPackage__,
	);
	useEffect(() => {
		if (skip) {
			setLoading(false);
			return;
		}

		setLoading(true);
		setError(undefined);
		setData(undefined);

		try {
			disposableRef.current = requestSubscription(environment, {
				subscription,
				variables,
				// `RelayWebSocketSubscriptionHandler` reads `cacheConfig.metadata.extensions`
				// and attaches them to the WS payload's `extensions` field.
				// cacheConfig is always set because useMemoObj coerces undefined → {}, so any
				// guard on `extensions` would always evaluate the same way. The consumer
				// (RelayWebSocketSubscriptionHandler) ignores an empty extensions object.
				cacheConfig: { metadata: { extensions } },
				onNext: (response: unknown) => {
					const responseData = response as TData;
					setData(responseData);
					setError(undefined);
					setLoading(false);
					callbacksRef.current?.onSubscriptionData?.({
						subscriptionData: { data: responseData, loading: false },
					});
				},
				onError: (err: Error) => {
					setError(err as ApolloErrorInterop);
					setLoading(false);
				},
				onCompleted: () => {
					setLoading(false);
					callbacksRef.current?.onSubscriptionComplete?.();
				},
			});
		} catch (err) {
			setError(err as ApolloErrorInterop);
			setLoading(false);
		}

		return () => {
			if (!disposableRef.current) return;
			disposableRef.current.dispose();
			disposableRef.current = null;
		};
	}, [environment, subscription, variables, extensions, skip]);

	return {
		data,
		loading,
		error,
	};
}
